// ─────────────────────────────────────────────────────────────
// SortBeat Daily — Deterministic Seeded RNG
// Uses mulberry32 — same algorithm as the Supabase Edge Function.
// Given the same seed string, always produces identical output.
// ─────────────────────────────────────────────────────────────
import type { Color } from '../types/game';
import { ALL_COLORS } from '../types/game';

/**
 * Hash a seed string to a 32-bit unsigned integer.
 * Uses the djb2 algorithm — fast, simple, well-distributed.
 */
function hashSeed(seed: string): number {
  let hash = 5381;
  for (let i = 0; i < seed.length; i++) {
    hash = ((hash << 5) + hash) ^ seed.charCodeAt(i);
    hash = hash >>> 0; // Keep as 32-bit unsigned
  }
  return hash;
}

/**
 * mulberry32 — a small, fast, high-quality 32-bit PRNG.
 * Returns a function that produces a float in [0, 1) on each call.
 *
 * @param seed - Any string (e.g. "2026-06-27")
 */
export function mulberry32(seed: string): () => number {
  let state = hashSeed(seed);
  return function () {
    state += 0x6d2b79f5;
    let z = state;
    z = Math.imul(z ^ (z >>> 15), z | 1);
    z ^= z + Math.imul(z ^ (z >>> 7), z | 61);
    return ((z ^ (z >>> 14)) >>> 0) / 0x100000000;
  };
}

/**
 * Return today's date as a YYYY-MM-DD string (UTC).
 * Used as the daily challenge seed key.
 */
export function todayDateString(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * Fisher-Yates shuffle using a provided seeded RNG.
 * Pure function — does not mutate the input array.
 */
export function seededShuffle<T>(arr: T[], rng: () => number): T[] {
  const copy = [...arr];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

// ── Onboarding ─────────────────────────────────────────────────────────────

/**
 * Fixed seed for the first-session onboarding board.
 * Must match the seed passed to buildOnboardingQueue — never use with buildQueue.
 */
export const ONBOARDING_SEED = 'sortbeat-onboarding-v1';

/**
 * Build a token queue guaranteed to produce a chain explosion within the
 * player's first 4 moves, even with minimal experience.
 *
 * Positions 0, 1, 3 = 'red' (3 tokens of the same colour).
 * If the player routes all reds to the same lane, the chain fires on move 4.
 * Positions 2, 4, 5 and beyond are seeded normally — the board still feels real.
 *
 * @param rng - Seeded RNG initialised with ONBOARDING_SEED via mulberry32().
 */
export function buildOnboardingQueue(rng: () => number): Color[] {
  const anchor: Color = 'red';
  const others = ALL_COLORS.filter(c => c !== anchor);

  // Anchor at 0, 1, 3 — natural "signal pattern" a new player notices:
  // red → red → X → red → the chain fires if they collect reds in one lane.
  const template: Color[] = [
    anchor,
    anchor,
    others[Math.floor(rng() * others.length)],
    anchor,
    others[Math.floor(rng() * others.length)],
    others[Math.floor(rng() * others.length)],
  ];

  // Positions 6-39 generated normally — rest of game plays like a daily board.
  const rest = Array.from({ length: 34 }, () =>
    ALL_COLORS[Math.floor(rng() * ALL_COLORS.length)]
  );

  return [...template, ...rest];
}
